import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Button, Grid, Input, InputProps, Loader, Text } from 'components/ui';
import { ApiClient } from 'lib/api/client';
import { PicSearchResponse } from 'lib/api/generated';
import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';

const VerifyButton = styled(Button)`
  display: flex;
  align-self: center;
  margin-top: 25px;
  background-color: #ffffff;
`;

const PICStatus = styled.div`
  margin: 8px;
  font-size: 12px;
  color: green;
`;

const ErrorText = styled(Text)`
  font-size: 14px;
  color: red;
`;

interface IVerifyPicProps {
  pic?: string;
  inputProps: InputProps;
  setValue: UseFormSetValue<any>;
  errors: any;
  mode?: 'address' | 'createorg';
  isRequired?: boolean;
}

const VerifyPIC = ({
  pic,
  setValue,
  inputProps,
  errors,
  isRequired,
  mode = 'createorg',
}: IVerifyPicProps) => {
  const [verifyPICData, setVerifyPICData] = useState<PicSearchResponse>();
  const [picError, setPICError] = useState<string>('');

  useEffect(() => {
    if (pic) {
      setPICError('');
      setVerifyPICData(undefined);
    }
  }, [pic]);

  const { mutate, isLoading: isPICVerifying } = useMutation(
    () =>
      new ApiClient().SearchApi.searchPicList({
        pic: pic || '',
      }),
    {
      onSuccess: (data: any) => {
        if (data?.data?.results.length > 0) {
          setVerifyPICData(data?.data?.results[0]);
          setValue('name', data?.data?.results[0]?.business_name, {
            shouldValidate: false,
          });
          toast.success('PIC verified successfully!');
        } else {
          mode === 'address'
            ? setPICError(
                'The PIC could not be found in the NLIS database but you may still proceed to add address'
              )
            : setPICError('The PIC could not be found in the NLIS database');
        }
      },
      onError: (error) => {
        if (!axios.isAxiosError(error)) return;
        if (error.response?.data.errors.pic) {
          setPICError(error.response?.data.errors.pic[0]);
        } else {
          throw error;
        }
      },
    }
  );

  const getVerifyStatus = () => {
    if (!isPICVerifying && verifyPICData === undefined) {
      return 'Verify';
    } else if (isPICVerifying) {
      return <Loader />;
    } else if (verifyPICData) {
      return 'Verified';
    }
  };

  const handleVerifyPic = () => {
    mutate();
  };

  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`);

  return (
    <>
      <Grid columns='4fr 1fr' gap={2} stackOnSmall>
        <Input
          {...inputProps}
          type='text'
          label='Property Identification Code (PIC)'
          isRequired={isRequired}
          placeholder='Property Identification Code'
          state={errors.pic ? 'error' : picError ? 'error' : undefined}
          message={errors.pic?.message || (mode === 'createorg' && picError)}
          {...(verifyPICData
            ? { icon: 'CheckGreenCircleIcon', iconAlign: 'right' }
            : {})}
          orgFontSize={3}
          isMobile={isMobile}
        />
        <VerifyButton
          size='small'
          type='button'
          variant='outline'
          data-cy='verifyPic'
          disabled={pic === '' || pic === undefined}
          onClick={handleVerifyPic}
        >
          {getVerifyStatus()}
        </VerifyButton>
      </Grid>
      {mode === 'address' && picError && <ErrorText>{picError}</ErrorText>}
      {verifyPICData && verifyPICData?.pic_status && (
        <PICStatus>PIC Status : {verifyPICData?.pic_status}</PICStatus>
      )}
    </>
  );
};

export default VerifyPIC;
